import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, ShoppingCart, X, Wallet, CreditCard, Award, Eye, Package, Plus, Minus, ExternalLink, Truck, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError, fileUrl, certUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, GradeBadge, Lightbox } from "@/components/common";

const SHOP_BRANDS = ["PSA", "Beckett", "TAG", "Degree", "AGS", "CGC"];
const CASE_MIN = 5;

export default function Shop() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { items: cart, add, remove, clear, total } = useCart();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState("ALL");
  const [section, setSection] = useState("cards");
  const [lightbox, setLightbox] = useState(null);
  const [detailIdx, setDetailIdx] = useState(0);
  const touchStart = useRef(null);
  const swiped = useRef(false);

  const load = () => api.get("/shop").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { setDetailIdx(0); }, [detail]);

  const detailPhotos = detail ? (detail.photos && detail.photos.length ? detail.photos : (detail.photo ? [detail.photo] : [])) : [];
  const goPhoto = (dir) => { if (detailPhotos.length > 1) setDetailIdx((i) => (i + dir + detailPhotos.length) % detailPhotos.length); };
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40 && detailPhotos.length > 1) { swiped.current = true; goPhoto(dx < 0 ? 1 : -1); }
    touchStart.current = null;
  };

  const checkout = async (useCredit) => {
    if (!user) { nav("/login"); return; }
    if (!cart.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        item_ids: cart.map((i) => i.id), origin_url: window.location.origin, use_credit: useCredit,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(data.message || "Purchase complete!");
      clear(); setOpen(false); setDetail(null); load();
    } catch (err) {
      const msg = apiError(err);
      if (msg === "SHIPPING_REQUIRED") { toast.error("Add your real name & shipping address first"); nav("/account"); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const buyNow = async (item, useCredit) => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", { item_ids: [item.id], origin_url: window.location.origin, use_credit: useCredit });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(data.message || "Purchase complete!");
      setDetail(null); load();
    } catch (err) {
      const msg = apiError(err);
      if (msg === "SHIPPING_REQUIRED") { toast.error("Add your real name & shipping address first"); nav("/account"); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  if (items === null) return <Loader label="Loading shop" />;

  const presentBrands = SHOP_BRANDS.filter((b) => items.some((i) => i.brand === b));
  const filtered = brand === "ALL" ? items : items.filter((i) => i.brand === brand);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white">Shop</h1>
          <p className="text-slate-400 mt-1">{section === "cards" ? "Hand-picked graded slabs — one of a kind. Once it's gone, it's gone." : "Protect your slabs. Pick your colors — 5 case minimum."}</p>
        </div>
        {section === "cards" && (
          <button onClick={() => setOpen(true)} className="relative card-surface rounded-xl px-4 py-2.5 text-white font-semibold flex items-center gap-2 hover:border-gold/40" data-testid="open-cart">
            <ShoppingCart className="w-5 h-5 text-gold" /> Cart
            {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-gold text-obsidian text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{cart.length}</span>}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 max-w-xs gap-2 p-1 rounded-2xl bg-vault/60 border border-gold/15 mb-6" data-testid="shop-section-toggle">
        <button onClick={() => setSection("cards")} data-testid="section-cards" className={`py-2.5 rounded-xl text-sm font-bold transition-colors ${section === "cards" ? "gold-gradient text-obsidian" : "text-slate-300 hover:text-white"}`}>Graded Cards</button>
        <button onClick={() => setSection("cases")} data-testid="section-cases" className={`py-2.5 rounded-xl text-sm font-bold transition-colors ${section === "cases" ? "gold-gradient text-obsidian" : "text-slate-300 hover:text-white"}`}>Slab Cases</button>
      </div>

      {section === "cases" ? <CasesShop user={user} nav={nav} /> : (
      <>
      {presentBrands.length > 0 && (
        <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-1 mb-6" data-testid="shop-brand-filters">
          {["ALL", ...presentBrands].map((b) => {
            const on = brand === b;
            return (
              <button key={b} onClick={() => setBrand(b)} data-testid={`brand-filter-${b.toLowerCase()}`}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide whitespace-nowrap border transition-colors ${on ? "gold-gradient text-obsidian border-transparent gold-glow" : "card-surface text-slate-300 border-gold/20 hover:border-gold/50"}`}>
                {b === "ALL" ? "All" : b}
                <span className={`ml-2 text-[10px] ${on ? "text-obsidian/60" : "text-gold/70"}`}>{b === "ALL" ? items.length : items.filter((i) => i.brand === b).length}</span>
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card-surface rounded-2xl p-16 text-center text-slate-400"><Store className="w-10 h-10 mx-auto text-gold/40 mb-3" />{items.length === 0 ? "No items listed yet." : `No ${brand === "ALL" ? "" : brand + " "}items right now.`}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
          {filtered.map((i) => {
            const inCart = cart.find((c) => c.id === i.id);
            const sold = i.status === "sold";
            return (
              <div key={i.id} className={`card-surface rounded-2xl overflow-hidden group ${sold ? "opacity-60" : ""}`} data-testid={`shop-item-${i.id}`}>
                <button onClick={() => setDetail(i)} className="relative aspect-[4/3] bg-vault w-full block overflow-hidden" data-testid={`shop-view-${i.id}`}>
                  {i.photo ? <img src={fileUrl(i.photo)} alt={i.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Award className="w-12 h-12" /></div>}
                  <div className="absolute top-2 right-2"><GradeBadge grade={[i.brand, i.grade].filter(Boolean).join(" ")} /></div>
                  {i.photos && i.photos.length > 1 && <div className="absolute top-2 left-2 glass rounded-full px-2 py-0.5 text-[10px] font-bold text-white border border-white/20 flex items-center gap-1" data-testid={`photo-count-${i.id}`}><Eye className="w-3 h-3" /> {i.photos.length}</div>}
                  <div className="absolute bottom-2 left-2 glass rounded-full px-2.5 py-1 text-[10px] font-bold text-gold border border-gold/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"><Eye className="w-3 h-3" /> View</div>
                  {sold && <div className="absolute inset-0 flex items-center justify-center"><span className="bg-slate-900/80 text-slate-300 font-black tracking-widest px-4 py-2 rounded-lg border border-slate-500/40 -rotate-6">SOLD</span></div>}
                </button>
                <div className="p-4">
                  <h3 onClick={() => setDetail(i)} className="font-display font-bold text-white text-base line-clamp-1 cursor-pointer hover:text-gold transition-colors">{i.title}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    {i.certificate_number && (certUrl(i.brand, i.certificate_number)
                      ? <a href={certUrl(i.brand, i.certificate_number)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] font-mono text-blue-400 hover:text-blue-300 hover:underline" data-testid={`cert-link-${i.id}`}>#{i.certificate_number}</a>
                      : <span className="text-[10px] font-mono text-slate-500">#{i.certificate_number}</span>)}
                  </div>
                  <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-emerald-400" data-testid={`free-ship-${i.id}`}><Truck className="w-3 h-3" /> Free shipping</span>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-gold font-mono font-bold">${i.price.toFixed(2)}</span>
                    {!sold && (
                      inCart
                        ? <button onClick={() => remove(i.id)} className="text-xs text-slate-400 border border-gold/20 rounded-lg px-3 py-1.5" data-testid={`remove-${i.id}`}>Remove</button>
                        : <button onClick={() => add(i)} className="text-xs gold-gradient text-obsidian font-bold rounded-lg px-3 py-1.5" data-testid={`add-${i.id}`}>Add</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cart drawer */}
      {open && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md glass border-l border-gold/20 h-full p-6 overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold text-white">Your Cart</h2>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {cart.length === 0 ? <p className="text-slate-400 text-center py-12">Your cart is empty.</p> : (
              <>
                <div className="space-y-3">
                  {cart.map((i) => (
                    <div key={i.id} className="flex items-center gap-3 card-surface rounded-xl p-3">
                      <div className="w-14 h-14 rounded-lg bg-vault overflow-hidden shrink-0">{i.photo && <img src={fileUrl(i.photo)} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{i.title}</p><p className="text-gold font-mono text-sm">${i.price.toFixed(2)}</p></div>
                      <button onClick={() => remove(i.id)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-gold/15 pt-4">
                  <span className="text-slate-400">Total</span>
                  <span className="font-display text-2xl font-black text-gold">${total.toFixed(2)}</span>
                </div>
                <div className="mt-4 space-y-3">
                  {user && user.store_credit >= total && (
                    <button onClick={() => checkout(true)} disabled={busy} className="w-full card-surface text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:border-gold/40" data-testid="cart-pay-credit"><Wallet className="w-5 h-5 text-gold" /> Pay with Store Credit</button>
                  )}
                  <button onClick={() => checkout(false)} disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="cart-checkout"><CreditCard className="w-5 h-5" /> Checkout with Card</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Item detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="shop-detail-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-5xl card-surface rounded-2xl overflow-hidden gold-glow-lg max-h-[92vh] overflow-y-auto custom-scrollbar">
            <button onClick={() => setDetail(null)} className="absolute top-3 right-3 z-10 glass rounded-full p-2 text-slate-300 hover:text-white" data-testid="shop-detail-close"><X className="w-5 h-5" /></button>
            <div className="grid md:grid-cols-[1.5fr_1fr]">
              {(() => {
                const photos = detailPhotos;
                const active = photos[detailIdx] || detail.photo;
                return (
                  <div className="bg-gradient-to-br from-vault to-obsidian flex flex-col">
                    <div className="relative flex-1" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                      <button type="button" onClick={() => { if (swiped.current) { swiped.current = false; return; } active && setLightbox(fileUrl(active)); }} className="group relative w-full h-full flex items-center justify-center p-4 sm:p-8 min-h-[360px] md:min-h-[460px]" data-testid="detail-image-btn">
                        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 40%, rgba(230,184,74,0.16), transparent 62%)" }} />
                        {active ? <img src={fileUrl(active)} alt={detail.title} className="relative max-h-[70vh] w-auto max-w-full object-contain rounded-lg shadow-2xl select-none pointer-events-none" draggable="false" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Award className="w-16 h-16" /></div>}
                        {active && <span className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs font-semibold text-white bg-black/50 backdrop-blur px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="w-3.5 h-3.5" /> Zoom</span>}
                        {detail.status === "sold" && <div className="absolute inset-0 flex items-center justify-center"><span className="bg-slate-900/80 text-slate-300 font-black tracking-widest px-5 py-2 rounded-lg border border-slate-500/40 -rotate-6">SOLD</span></div>}
                      </button>
                      {photos.length > 1 && (
                        <>
                          <button type="button" onClick={() => goPhoto(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass border border-white/15 text-white flex items-center justify-center hover:bg-white/10" data-testid="detail-prev"><ChevronLeft className="w-5 h-5" /></button>
                          <button type="button" onClick={() => goPhoto(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass border border-white/15 text-white flex items-center justify-center hover:bg-white/10" data-testid="detail-next"><ChevronRight className="w-5 h-5" /></button>
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="detail-dots">
                            {photos.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === detailIdx ? "bg-gold" : "bg-white/30"}`} />)}
                          </div>
                        </>
                      )}
                    </div>
                    {photos.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto custom-scrollbar px-4 pb-4 sm:px-6" data-testid="detail-thumbnails">
                        {photos.map((p, i) => (
                          <button key={p} type="button" onClick={() => setDetailIdx(i)} className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === detailIdx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`detail-thumb-${i}`}>
                            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2"><GradeBadge grade={[detail.brand, detail.grade].filter(Boolean).join(" ")} />{detail.status === "sold" && <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-slate-500/15 text-slate-300">SOLD</span>}</div>
                <h2 className="font-display text-2xl font-black text-white">{detail.title}</h2>
                {detail.certificate_number && (
                  <p className="text-xs font-mono mt-1 text-slate-500">CERT {certUrl(detail.brand, detail.certificate_number)
                    ? <a href={certUrl(detail.brand, detail.certificate_number)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline inline-flex items-center gap-1" data-testid="detail-cert-link">#{detail.certificate_number} <ExternalLink className="w-3 h-3" /></a>
                    : <span>#{detail.certificate_number}</span>}
                  </p>
                )}
                {detail.certificate_number && certUrl(detail.brand, detail.certificate_number) && <p className="text-[10px] text-slate-500 mt-1">Tap the cert # to verify on {detail.brand}'s official site.</p>}
                {detail.description && <p className="text-slate-300 text-sm mt-4 leading-relaxed">{detail.description}</p>}
                <p className="font-display text-3xl font-black text-gold mt-5">${detail.price.toFixed(2)}</p>
                <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-emerald-400" data-testid="detail-free-ship"><Truck className="w-4 h-4" /> Free shipping on this item</span>
                {detail.status !== "sold" && (
                  <div className="mt-5 space-y-3">
                    {cart.find((c) => c.id === detail.id)
                      ? <button onClick={() => remove(detail.id)} className="w-full card-surface text-slate-300 font-bold py-3 rounded-xl border border-gold/20" data-testid="detail-remove">Remove from cart</button>
                      : <button onClick={() => { add(detail); toast.success("Added to cart"); }} className="w-full card-surface text-white font-bold py-3 rounded-xl hover:border-gold/40 flex items-center justify-center gap-2" data-testid="detail-add"><ShoppingCart className="w-5 h-5 text-gold" /> Add to cart</button>}
                    {user && user.store_credit >= detail.price && (
                      <button onClick={() => buyNow(detail, true)} disabled={busy} className="w-full card-surface text-white font-bold py-3 rounded-xl border border-gold/20 flex items-center justify-center gap-2" data-testid="detail-buy-credit"><Wallet className="w-5 h-5 text-gold" /> Buy with Store Credit</button>
                    )}
                    <button onClick={() => buyNow(detail, false)} disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="detail-buy"><CreditCard className="w-5 h-5" /> Buy now with Card</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function CasesShop({ user, nav }) {
  const [cases, setCases] = useState(null);
  const [sel, setSel] = useState({});
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/cases").then(({ data }) => setCases(data)).catch(() => setCases([]));
  useEffect(() => { load(); }, []);

  const totalQty = cart.reduce((s, l) => s + l.quantity, 0);
  const totalPrice = cart.reduce((s, l) => s + l.price * l.quantity, 0);

  const addToCart = (c) => {
    const color = sel[c.id];
    if (!color) { toast.error("Pick a color first"); return; }
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.case_id === c.id && l.color === color);
      if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], quantity: n[idx].quantity + 1 }; return n; }
      return [...prev, { case_id: c.id, title: c.title, photo: c.photo, price: c.price, color, quantity: 1 }];
    });
    toast.success(`Added ${c.title} (${color})`);
  };
  const setQty = (i, q) => setCart((prev) => prev.map((l, idx) => idx === i ? { ...l, quantity: Math.max(1, q) } : l));
  const removeLine = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const checkout = async (useCredit) => {
    if (!user) { nav("/login"); return; }
    if (totalQty < CASE_MIN) { toast.error(`Minimum ${CASE_MIN} cases — you have ${totalQty}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cases/checkout", {
        lines: cart.map((l) => ({ case_id: l.case_id, color: l.color, quantity: l.quantity })),
        origin_url: window.location.origin, use_credit: useCredit,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(data.message || "Purchase complete!");
      setCart([]); setOpen(false);
    } catch (err) {
      const msg = apiError(err);
      if (msg === "SHIPPING_REQUIRED") { toast.error("Add your real name & shipping address first"); nav("/account"); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  if (cases === null) return <Loader label="Loading cases" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-400"><span className={totalQty >= CASE_MIN ? "text-emerald-400" : "text-amber-400"}>{totalQty}</span> / {CASE_MIN} minimum in cart</p>
        <button onClick={() => setOpen(true)} className="relative card-surface rounded-xl px-4 py-2 text-white text-sm font-semibold flex items-center gap-2 hover:border-gold/40" data-testid="open-cases-cart">
          <ShoppingCart className="w-4 h-4 text-gold" /> Cart
          {totalQty > 0 && <span className="absolute -top-2 -right-2 bg-gold text-obsidian text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{totalQty}</span>}
        </button>
      </div>

      {cases.length === 0 ? (
        <div className="card-surface rounded-2xl p-16 text-center text-slate-400" data-testid="cases-empty"><Package className="w-10 h-10 mx-auto text-gold/40 mb-3" />No cases listed yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cases-grid">
          {cases.map((c) => {
            const inStock = (c.colors || []).filter((x) => x.in_stock);
            const soldOut = inStock.length === 0;
            return (
              <div key={c.id} className={`card-surface rounded-2xl overflow-hidden ${soldOut ? "opacity-60" : ""}`} data-testid={`case-item-${c.id}`}>
                <div className="aspect-video bg-vault">{c.photo ? <img src={fileUrl(c.photo)} alt={c.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Package className="w-10 h-10" /></div>}</div>
                <div className="p-4">
                  <h3 className="font-display font-bold text-white">{c.title}</h3>
                  {c.description && <p className="text-slate-400 text-xs mt-1 line-clamp-2">{c.description}</p>}
                  <p className="text-gold font-mono font-bold mt-2">${c.price.toFixed(2)} <span className="text-slate-500 text-xs font-sans">/ case</span></p>
                  <div className="mt-3 flex gap-2">
                    <select value={sel[c.id] || ""} onChange={(e) => setSel({ ...sel, [c.id]: e.target.value })} disabled={soldOut}
                      className="flex-1 bg-vault border border-gold/20 rounded-xl px-3 py-2 text-white text-sm focus:border-gold focus:outline-none disabled:opacity-50" data-testid={`case-color-select-${c.id}`}>
                      <option value="">{soldOut ? "Sold out" : "Choose color"}</option>
                      {(c.colors || []).map((col) => <option key={col.name} value={col.name} disabled={!col.in_stock}>{col.name}{col.in_stock ? "" : " (out of stock)"}</option>)}
                    </select>
                    <button onClick={() => addToCart(c)} disabled={soldOut} className="gold-gradient text-obsidian font-bold rounded-xl px-4 text-sm disabled:opacity-50" data-testid={`case-add-${c.id}`}>Add</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="cases-cart-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md glass border-l border-gold/20 h-full p-6 overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold text-white">Case Cart</h2>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {cart.length === 0 ? <p className="text-slate-400 text-center py-12">Your case cart is empty.</p> : (
              <>
                <div className="space-y-3">
                  {cart.map((l, i) => (
                    <div key={`${l.case_id}-${l.color}`} className="flex items-center gap-3 card-surface rounded-xl p-3" data-testid={`case-line-${i}`}>
                      <div className="w-12 h-12 rounded-lg bg-vault overflow-hidden shrink-0">{l.photo && <img src={fileUrl(l.photo)} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{l.title}</p><p className="text-gold font-mono text-xs">{l.color} · ${l.price.toFixed(2)}</p></div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setQty(i, l.quantity - 1)} className="w-7 h-7 rounded-lg bg-vault border border-gold/20 text-gold flex items-center justify-center" data-testid={`case-line-minus-${i}`}><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center text-white text-sm font-mono">{l.quantity}</span>
                        <button onClick={() => setQty(i, l.quantity + 1)} className="w-7 h-7 rounded-lg bg-vault border border-gold/20 text-gold flex items-center justify-center" data-testid={`case-line-plus-${i}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <button onClick={() => removeLine(i)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-gold/15 pt-4">
                  <span className="text-slate-400">{totalQty} case{totalQty !== 1 ? "s" : ""}</span>
                  <span className="font-display text-2xl font-black text-gold">${totalPrice.toFixed(2)}</span>
                </div>
                {totalQty < CASE_MIN && <p className="text-amber-400 text-xs mt-2" data-testid="case-min-warning">Add {CASE_MIN - totalQty} more — {CASE_MIN} case minimum to check out.</p>}
                <div className="mt-4 space-y-3">
                  {user && user.store_credit >= totalPrice && (
                    <button onClick={() => checkout(true)} disabled={busy || totalQty < CASE_MIN} className="w-full card-surface text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:border-gold/40 disabled:opacity-50" data-testid="cases-pay-credit"><Wallet className="w-5 h-5 text-gold" /> Pay with Store Credit</button>
                  )}
                  <button onClick={() => checkout(false)} disabled={busy || totalQty < CASE_MIN} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50" data-testid="cases-checkout"><CreditCard className="w-5 h-5" /> Checkout with Card</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
